// pages/SchedulePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import PageShell from "../components/PageShell";
import { useNavigate, useSearchParams } from "react-router-dom";

const GAME_FILTER_LABEL = "Game";
const ALL_GAMES_LABEL = "All";

/**
 * Parse a Date from event fields using local time.
 * Falls back in order: race -> quali -> practice -> 00:00
 */
function getEventDateTime(e) {
  const time = (e.race || e.quali || e.practice || "00:00").trim();
  return new Date(`${e.date}T${time}`);
}

/**
 * Format local datetime nicely (no seconds) for display.
 */
function formatLocalDateTime(dt, tz) {
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dt);
  } catch {
    return dt.toLocaleString();
  }
}

function ScheduleRowCard({ e, i, isPast, isNext, tz, currentRef }) {
  const dt = getEventDateTime(e);
  const navigate = useNavigate();

  const statusChip = isNext ? (
    <Chip color="primary" size="large" label="Next race" />
  ) : isPast ? (
    <Chip size="large" label="Completed"/>
  ) : (
    <Chip size="large" label="Upcoming" variant="outlined" />
  );
 
  // low-noise: only show what matters on mobile
  return (
    <Box
      ref={currentRef}
      sx={{
        px: 1.5,
        py: 1.25,
        opacity: isPast ? 0.55 : 1,
        ...(isNext && {
          scrollMarginTop: { xs: 148, md: 160 },
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main} inset`,
          background: "linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0) 70%)",
        }),
      }}
      onClick={() => isPast && navigate(`/results`)}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Box
          sx={{
            width: 34,
            textAlign: "center",
            fontWeight: 900,
            fontSize: 14,
            opacity: 0.9,
            flex: "0 0 auto",
            mt: 0.25,
          }}
        >
          {e.round ?? i + 1}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 900 }} noWrap>
              {e.track}
            </Typography>
            {statusChip}
          </Stack>

          <Typography variant="body2" color="text.secondary" noWrap>
            {e.game} • {e.season} • {e.type}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
            <Chip size="small" label={formatLocalDateTime(dt, tz)} />
            {e.race && <Chip size="small" label={`Race ${e.race}`} color={isNext ? "primary" : "default"} />}
            {!e.race && e.quali && <Chip size="small" label={`Quali ${e.quali}`} variant="outlined" />}
            {!e.race && !e.quali && e.practice && (
              <Chip size="small" label={`Practice ${e.practice}`} variant="outlined" />
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Schedule page
 * Props: { data }
 */
export default function SchedulePage({ data }) {
  const intro =
    data?.schedule?.intro ??
    data?.scheduleIntro ??
    "Upcoming events, practice, quali, and race times.";

  const tz = data?.schedule?.timezone ?? data?.meta?.timezone ?? "Africa/Johannesburg";
  const eventsRaw = useMemo(() => data?.schedule?.events ?? [], [data?.schedule?.events]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mobileCurrentRef = useRef(null);
  const desktopCurrentRef = useRef(null);

  const games = useMemo(
    () =>
      [...new Set(eventsRaw.map((event) => event.game).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [eventsRaw]
  );
  const requestedGame = searchParams.get("game") ?? "";
  const selectedGame = games.includes(requestedGame) ? requestedGame : "";

  const handleGameChange = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    if (event.target.value) nextParams.set("game", event.target.value);
    else nextParams.delete("game");
    setSearchParams(nextParams);
  };

  // Sort events by their effective datetime (date + race/quali/practice)
  const events = useMemo(() => {
    const copy = selectedGame
      ? eventsRaw.filter((event) => event.game === selectedGame)
      : [...eventsRaw];
    copy.sort((a, b) => getEventDateTime(a) - getEventDateTime(b));
    return copy;
  }, [eventsRaw, selectedGame]);

  const [now] = useState(() => new Date());

  // Find the first event that is now or in the future — that’s the "next race"
  const nextIndex = useMemo(() => {
    return events.findIndex((ev) => getEventDateTime(ev) >= now);
  }, [events, now]);

  const currentIndex = nextIndex === -1 ? events.length - 1 : nextIndex;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const currentElement = [mobileCurrentRef.current, desktopCurrentRef.current].find(
        (element) => element && element.getClientRects().length > 0
      );

      currentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentIndex]);

  return (
    <PageShell
      title="Schedule"
      chips={[tz]}
      bannerImage="calendar.png"
      contentSx={{ overflow: "visible" }}
    >
      <Typography sx={{ mb: 2 }}>{intro}</Typography>

      <Box
        sx={{
          position: "sticky",
          top: { xs: 60, md: 72 },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: "background.paper",
          py: 1,
          mb: 1,
        }}
      >
        <FormControl sx={{ minWidth: { xs: "100%", sm: 280 } }}>
          <InputLabel id="schedule-game-filter-label" shrink>
            {GAME_FILTER_LABEL}
          </InputLabel>
          <Select
            labelId="schedule-game-filter-label"
            id="schedule-game-filter"
            value={selectedGame}
            label={GAME_FILTER_LABEL}
            displayEmpty
            renderValue={(value) => value || ALL_GAMES_LABEL}
            onChange={handleGameChange}
          >
            <MenuItem value="">{ALL_GAMES_LABEL}</MenuItem>
            {games.map((game) => (
              <MenuItem key={game} value={game}>
                {game}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {events.length > 0 ? (
        <>
          {/* Mobile-first list (low noise) */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
              {events.map((e, i) => {
                const dt = getEventDateTime(e);
                const isPast = dt < now && (nextIndex === -1 ? true : i < nextIndex);
                const isNext = i === nextIndex || (nextIndex === -1 && i === events.length - 1);
                return (
                  <React.Fragment key={`${e.round}-${e.track}-${e.date}-${i}`}>
                    <ScheduleRowCard
                      e={e}
                      i={i}
                      isPast={isPast}
                      isNext={isNext}
                      tz={tz}
                      currentRef={isNext ? mobileCurrentRef : undefined}
                    />
                    {i !== events.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </Paper>
          </Box>

          {/* Desktop/tablet table (aligned + less clutter) */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Game</TableCell>
                    <TableCell>Season</TableCell>
                    <TableCell>Rnd</TableCell>
                    <TableCell>Track</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Practice</TableCell>
                    <TableCell>Quali</TableCell>
                    <TableCell>Race</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((e, i) => {
                    const dt = getEventDateTime(e);
                    const isPast = dt < now && (nextIndex === -1 ? true : i < nextIndex);
                    const isNext = i === nextIndex || (nextIndex === -1 && i === events.length - 1);

                    return (
                      <TableRow
                        ref={isNext ? desktopCurrentRef : undefined}
                        key={`${e.round}-${e.track}-${e.date}-${i}`}
                        hover
                        sx={{
                          opacity: isPast ? 0.6 : 1,
                          transition: "opacity 200ms ease, transform 200ms ease, box-shadow 200ms ease",
                          ...(isNext && {
                            position: "relative",
                            scrollMarginTop: { xs: 148, md: 160 },
                            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main} inset`,
                            background:
                              "linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0) 60%)",
                          }),
                        }}
                        onClick={() => isPast && navigate(`/results`)}
                      >
                        <TableCell>{e.game}</TableCell>
                        <TableCell>{e.season}</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>{e.round}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{e.track}</TableCell>
                        <TableCell>{e.date}</TableCell>
                        <TableCell>{e.type}</TableCell>
                        <TableCell>{e.practice || "-"}</TableCell>
                        <TableCell>{e.quali || "-"}</TableCell>
                        <TableCell>
                          {e.race ? (
                            <Chip
                              size="small"
                              label={e.race}
                              color={isNext ? "primary" : "default"}
                              variant={isNext ? "filled" : "outlined"}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </>
      ) : null}
    </PageShell>
  );
}
